/**
 * PayReceiptPage — public receipt page shown after a customer completes
 * (or cancels) a payment via Tamara / Stripe / Tabby / Ziina.
 *
 * Route:  /pay/receipt/:invoiceId  (PUBLIC — no auth required)
 *
 * This is the FALLBACK receipt page. It's used when a platform hasn't
 * deployed its own /payment-success route yet. Once the platform deploys
 * their custom page and admin configures the platform's `success_url`,
 * the payment gateways will redirect there directly instead of here.
 *
 * The page is rendered with the ORIGINATING platform's branding (logo +
 * accent colour) so the customer sees something familiar even though
 * we're serving it from the admin backend.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { DirhamPrice } from '../components/DirhamPrice';
import { TrnBadge } from '../components/TrnBadge';
import {
  CheckCircle2, XCircle, Clock, RefreshCw, Printer, Home, Loader2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const METHOD_LABELS = {
  cash:          { ar: 'نقدي',          en: 'Cash' },
  bank_transfer: { ar: 'تحويل بنكي',    en: 'Bank Transfer' },
  check:         { ar: 'شيك',           en: 'Check' },
  credit_card:   { ar: 'بطاقة ائتمان',  en: 'Credit Card' },
  tamara:        { ar: 'تمارا',         en: 'Tamara' },
  tabby:         { ar: 'تابي',          en: 'Tabby' },
  stripe:        { ar: 'بطاقة (Stripe)', en: 'Card (Stripe)' },
  ziina:         { ar: 'زينة',          en: 'Ziina' },
};

// Simple language switch — default AR based on Accept-Language would be ideal,
// but for a "public" page we just accept ?lang= URL param.
export default function PayReceiptPage() {
  const { invoiceId } = useParams();
  const [search] = useSearchParams();
  const state = (search.get('state') || 'success').toLowerCase();
  const lang = (search.get('lang') || 'ar').toLowerCase();
  const isAr = lang === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pollCount, setPollCount] = useState(0);

  const fetchReceipt = async (isPoll = false) => {
    if (isPoll) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API_URL}/api/public/receipt/${invoiceId}`);
      setData(r.data);
    } catch (e) {
      if (e.response?.status === 404) {
        setError('not_found');
      } else {
        setError('generic');
      }
    } finally {
      if (isPoll) setRefreshing(false); else setLoading(false);
    }
  };

  useEffect(() => { fetchReceipt(); /* eslint-disable-next-line */ }, [invoiceId]);

  // If invoice is still "unpaid" while the customer is on the success page,
  // Tamara probably hasn't fired the webhook yet — poll every 4s (up to 5 tries).
  useEffect(() => {
    if (!data || data.status !== 'unpaid' || state !== 'success' || pollCount >= 5) return;
    const timer = setTimeout(() => {
      fetchReceipt(true);
      setPollCount(c => c + 1);
    }, 4000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [data, state, pollCount]);

  useEffect(() => {
    document.documentElement.dir = isAr ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
    if (data?.invoice_no) {
      document.title = `${data.invoice_no} — ${t('إيصال دفع', 'Payment receipt')}`;
    }
  }, [data, isAr, lang]);

  // ── Banner state → colour + icon + title ─────────────────────────
  const banner = useMemo(() => {
    if (state === 'cancelled') {
      return { color: '#f59e0b', Icon: Clock, titleAr: 'تم إلغاء الدفع', titleEn: 'Payment cancelled' };
    }
    if (state === 'failed') {
      return { color: '#ef4444', Icon: XCircle, titleAr: 'فشل الدفع', titleEn: 'Payment failed' };
    }
    if (data?.status === 'paid') {
      return { color: '#10b981', Icon: CheckCircle2, titleAr: 'تم الدفع بنجاح', titleEn: 'Payment successful' };
    }
    if (data?.status === 'partially_paid') {
      return { color: '#3b82f6', Icon: CheckCircle2, titleAr: 'مدفوعة جزئياً', titleEn: 'Partially paid' };
    }
    return { color: '#f59e0b', Icon: Clock, titleAr: 'قيد المعالجة', titleEn: 'Processing' };
  }, [data, state]);

  const platformColor = data?.platform_color || '#d4af37';
  const homeUrl = data?.platform_home_url || '/';

  // ── Loading / error states ───────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303] text-white flex items-center justify-center p-6">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 animate-spin text-white/40" />
          <p className="text-white/60 text-sm">{t('جارٍ تحميل الإيصال...', 'Loading receipt...')}</p>
        </div>
      </div>
    );
  }

  if (error === 'not_found') {
    return (
      <div className="min-h-screen bg-[#030303] text-white flex items-center justify-center p-6" dir={isAr ? 'rtl' : 'ltr'}>
        <div className="max-w-md text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">{t('الفاتورة غير موجودة', 'Invoice not found')}</h1>
          <p className="text-white/50 mb-6">
            {t('الرقم غير صحيح أو تم حذف الفاتورة', 'The invoice id is invalid or has been removed')}
          </p>
          <a href="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#d4af37] text-black rounded-lg font-bold">
            <Home className="w-4 h-4" /> {t('الصفحة الرئيسية', 'Home')}
          </a>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#030303] text-white flex items-center justify-center p-6" dir={isAr ? 'rtl' : 'ltr'}>
        <div className="max-w-md text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">{t('حدث خطأ', 'Something went wrong')}</h1>
          <button
            onClick={() => fetchReceipt()}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#d4af37] text-black rounded-lg font-bold"
            data-testid="receipt-retry">
            <RefreshCw className="w-4 h-4" /> {t('إعادة المحاولة', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { Icon } = banner;

  return (
    <div className="min-h-screen bg-[#030303] text-white py-8 px-4" dir={isAr ? 'rtl' : 'ltr'}
         data-testid="receipt-page">
      <div className="max-w-2xl mx-auto">
        {/* Platform brand */}
        <div className="flex items-center gap-3 mb-6">
          {data.platform_logo ? (
            <img src={data.platform_logo} alt="" className="h-10 w-auto" />
          ) : (
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: platformColor }} />
          )}
          <span className="text-white/60 text-sm">
            {isAr ? data.platform_name_ar : data.platform_name_en}
          </span>
          <button
            onClick={() => (window.location.search = `?state=${state}&lang=${isAr ? 'en' : 'ar'}`)}
            className="ms-auto text-white/40 text-xs hover:text-white/80 border border-white/10 rounded px-2 py-1"
            data-testid="receipt-lang-toggle"
          >
            {isAr ? 'English' : 'العربية'}
          </button>
        </div>

        {/* iter-277 — TRN chip. Shows the immutable snapshot stored on the
           receipt when the invoice was issued, so historic receipts keep
           the same number even if the active TRN later changes. */}
        {data.company_trn && (
          <div className="mb-4">
            <TrnBadge value={data.company_trn} language={isAr ? 'ar' : 'en'} testId="receipt-trn-badge" />
          </div>
        )}

        {/* Banner */}
        <div className="flex items-center gap-4 p-5 rounded-2xl mb-6"
             style={{ backgroundColor: `${banner.color}15`, border: `1px solid ${banner.color}40` }}>
          <Icon className="w-10 h-10 flex-shrink-0" style={{ color: banner.color }} />
          <div>
            <h1 className="text-xl md:text-2xl font-bold" style={{ color: banner.color }}>
              {isAr ? banner.titleAr : banner.titleEn}
            </h1>
            <div className="text-white/60 text-sm mt-1">
              {t('رقم الإيصال', 'Receipt no.')}: <b className="text-white font-mono">{data.invoice_no}</b>
            </div>
          </div>
          {refreshing && <Loader2 className="w-5 h-5 animate-spin text-white/40 ms-auto" />}
        </div>

        {/* Pending notice (webhook not yet fired) */}
        {data.status === 'unpaid' && state === 'success' && (
          <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs flex items-center gap-2">
            <Clock className="w-4 h-4" />
            {t('الدفع قيد المعالجة، جارٍ التحقق من البوابة…',
               'Payment is being processed, verifying with the gateway…')}
            {pollCount >= 5 && (
              <button
                onClick={() => { setPollCount(0); fetchReceipt(true); }}
                className="ms-auto underline"
              >
                {t('تحديث الآن', 'Refresh now')}
              </button>
            )}
          </div>
        )}

        {/* Customer */}
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 mb-4">
          <h3 className="text-white font-bold text-sm mb-3">{t('بيانات العميل', 'Customer')}</h3>
          <Row lbl={t('الاسم', 'Name')}  val={data.customer?.name} />
          {data.customer?.email && <Row lbl={t('البريد', 'Email')} val={data.customer.email} />}
          {data.customer?.phone && <Row lbl={t('الهاتف', 'Phone')} val={data.customer.phone} />}
        </div>

        {/* Services */}
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 mb-4">
          <h3 className="text-white font-bold text-sm mb-3">{t('تفاصيل الخدمات', 'Services')}</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-white/50 text-xs">
                <th className={isAr ? 'text-right pb-2' : 'text-left pb-2'}>{t('الخدمة', 'Service')}</th>
                <th className={isAr ? 'text-right pb-2' : 'text-left pb-2'}>{t('الوصف', 'Description')}</th>
                <th className={isAr ? 'text-left pb-2' : 'text-right pb-2'}>{t('المبلغ', 'Amount')}</th>
              </tr>
            </thead>
            <tbody>
              {(data.services || []).map((s, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="py-2 text-white/90">{isAr ? s.service_type_ar : s.service_type_en}</td>
                  <td className="py-2 text-white/60 text-xs">{s.description}</td>
                  <td className={`py-2 font-mono ${isAr ? 'text-left' : 'text-right'}`}>{Number(s.amount || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-4 pt-4 border-t border-white/10 space-y-1.5">
            <Row lbl={t('المجموع قبل الضريبة', 'Subtotal')} val={`$<DirhamPrice value={data.subtotal} decimals={2} size="sm" />`} />
            {data.tax_amount > 0 && (
              <Row lbl={`${t('الضريبة', 'VAT')} (${data.tax_percent}%)`} val={`+ $<DirhamPrice value={data.tax_amount} decimals={2} size="sm" />`} />
            )}
            <div className="flex justify-between pt-2 border-t border-white/15">
              <span className="text-white font-bold">{t('الإجمالي', 'Total')}</span>
              <span className="text-2xl font-bold font-mono" style={{ color: platformColor }}>
                <DirhamPrice value={data.total} decimals={2} size="sm" />
              </span>
            </div>
            {data.paid_amount > 0 && (
              <Row lbl={t('المدفوع', 'Paid')}
                   val={<span className="text-emerald-400 font-mono"><DirhamPrice value={data.paid_amount} decimals={2} size="sm" /></span>} />
            )}
            {data.remaining_amount > 0.01 && (
              <Row lbl={t('المتبقي', 'Remaining')}
                   val={<span className="text-red-400 font-mono"><DirhamPrice value={data.remaining_amount} decimals={2} size="sm" /></span>} />
            )}
          </div>
        </div>

        {/* Payment method + status */}
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 mb-6 space-y-1.5">
          <Row lbl={t('طريقة الدفع', 'Payment method')}
               val={METHOD_LABELS[data.payment_method]?.[isAr ? 'ar' : 'en'] || data.payment_method} />
          <Row lbl={t('الحالة', 'Status')}
               val={<span className="px-2 py-0.5 rounded-full text-xs"
                          style={{ backgroundColor: `${banner.color}30`, color: banner.color }}>
                     {data.status}
                   </span>} />
          {data.paid_at && (
            <Row lbl={t('تاريخ الدفع', 'Paid at')} val={data.paid_at.slice(0, 19).replace('T', ' ')} />
          )}
        </div>

        {/* Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 print:hidden">
          <button
            onClick={() => window.print()}
            className="flex items-center justify-center gap-2 h-12 px-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold"
            data-testid="receipt-print"
          >
            <Printer className="w-4 h-4" /> {t('طباعة', 'Print')}
          </button>
          <a
            href={homeUrl}
            className="flex items-center justify-center gap-2 h-12 px-4 rounded-xl text-black font-bold"
            style={{ backgroundColor: platformColor }}
            data-testid="receipt-home"
          >
            <Home className="w-4 h-4" /> {t('العودة للصفحة الرئيسية', 'Back to home')}
          </a>
        </div>

        <div className="mt-6 pt-4 border-t border-white/5 text-center text-white/30 text-xs">
          {t('صفحة مؤقتة — إذا كنت مسؤول المنصة، اضبط رابط استلام الدفع من admin ← إعدادات القسم المالي ← روابط المنصات',
             'Fallback page — if you\'re the platform admin, configure your custom Payment Success URL from admin → Finance Settings → Platform URLs')}
        </div>
      </div>
    </div>
  );
}


function Row({ lbl, val }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-white/50">{lbl}</span>
      <span className="text-white/90">{val}</span>
    </div>
  );
}
